"use client"
import * as React from "react"
import Link from "next/link"
import { cn } from "@/lib/utils"
import { InternalLink } from "./internal-link"
import { BUTTON_IDS } from "@/lib/constants"
import { isAppHypertaskUrl } from "@/lib/redirect-utils"
import { handleInstantLogin, handleEmailSignup } from "@/lib/auth-utils"
import { usePageSettings } from "@/components/contexts/PageSettingsContext"
import { Send } from "lucide-react"
import { EmailSignupSuccess } from "./email-signup-success"

// Re-export mockAxios for backward compatibility
export { mockAxios } from '@/lib/auth-utils'

// Google logo SVG component - supports white, black, or colorful (default)
type GoogleLogoColor = 'white' | 'black' | 'colorful'

interface GoogleLogoProps {
  color?: GoogleLogoColor
}

const SvgPathColors ={
  white: {
    path1: 'white',
    path2: 'white',
    path3: 'white',
    path4: 'white',
  },
  black: {
    path1: 'black',
    path2: 'black',
    path3: 'black',
    path4: 'black',
  },
  colorful: {
    path1: '#4285F4',
    path2: '#34A853',
    path3: '#FBBC05',
    path4: '#EA4335',
  },
}

const GoogleLogo = ({ color = 'colorful' }: GoogleLogoProps) => {

  const svgPathColors = SvgPathColors[color]
  return (
    <svg className="w-3.5 h-3.5 md:w-4 md:h-4" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
          fill={svgPathColors.path1}
        />
        <path
          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
          fill={svgPathColors.path2}
        />
        <path
          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
          fill={svgPathColors.path3}
        />
        <path
          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
          fill={svgPathColors.path4}
        />
      </svg>
  )
}

export interface CTAButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode
  href?: string
  external?: boolean
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost'
  size?: 'header' | 'hero' | 'section' | 'sm' | 'lg'
  className?: string,
  id?: string
  enableGoogleLogin?: boolean
  showGoogleLogo?: boolean
  googleLogoColor?: 'white' | 'black' | 'colorful'
  usedInHeader?: boolean
}

// Helper: determine if this is a "login metric" (should get id='get-started-button')
// Now: if it leads user to app.hypertask.ai, that's it
function isLoginMetric(href?: string): boolean {
  if (!href) return false
  try {
    // If absolute URL, check host and must include 'login'
    if (href.startsWith("http")) {
      const url = new URL(href)
      return url.hostname === "app.hypertask.ai" && href.toLowerCase().includes("login")
    }
    // If relative, not a login metric
    return false
  } catch {
    return false
  }
}

// Helper: determine if this is an app.hypertask.ai URL that needs PostHog enhancement
// Uses centralized utility from redirect-utils

export const CTAButton = React.forwardRef<HTMLButtonElement | HTMLAnchorElement, CTAButtonProps>(
  ({ 
    children, 
    className, 
    href, 
    external, 
    variant = "primary", 
    size = "hero",
    id,
    enableGoogleLogin = false,
    onClick,
    showGoogleLogo = true,
    googleLogoColor = 'colorful',
    usedInHeader = false,
    ...props 
  }, ref) => {
    // Use prop color if provided, otherwise default to 'black'
    const logoColor = googleLogoColor || 'colorful'
    
    // Get email signup settings from context
    const { enableEmailSignup = false, emailSignupVariant = 'basic' } = usePageSettings()
    
    // Email signup state - lean and instant always show input, basic starts with button
    const [showEmailInput, setShowEmailInput] = React.useState(false)
    const [email, setEmail] = React.useState('')
    const [isLoading, setIsLoading] = React.useState(false)
    const [error, setError] = React.useState<string | null>(null)
    const [success, setSuccess] = React.useState(false)
    const emailInputRef = React.useRef<HTMLInputElement>(null)
    
    // Auto-focus email input for lean and instant variants
    React.useEffect(() => {
      if ((emailSignupVariant === 'lean' || emailSignupVariant === 'instant') && showEmailInput && emailInputRef.current) {
        // Small delay to ensure DOM is ready
        setTimeout(() => {
          emailInputRef.current?.focus()
        }, 100)
      }
    }, [emailSignupVariant, showEmailInput])
    
    // Email validation
    const validateEmail = (email: string): boolean => {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return emailRegex.test(email)
    }
    
    // Handle email signup submission
    const handleEmailSubmit = async (e: React.FormEvent) => {
      e.preventDefault()
      setError(null)
      
      if (!email.trim()) {
        setError('Please enter your email address')
        return
      }
      
      if (!validateEmail(email)) {
        setError('Please enter a valid email address')
        return
      }
      
      setIsLoading(true)
      
      try {
        if (emailSignupVariant === 'instant') {
          // Instant login - call API and redirect
          const posthogInstance = typeof window !== 'undefined' ? (window as any).posthog : undefined
          const redirectUrl = await handleInstantLogin(email, posthogInstance)
          
          // Add a small delay to show success before redirect
          setTimeout(() => {
            window.location.href = redirectUrl
          }, 500)
        } else {
          // Basic/Lean - call email signup API and show success message
          await handleEmailSignup(email)
          setSuccess(true)
          setEmail('')
        }
      } catch (err: any) {
        console.error('❌ Error:', err)
        setError(err?.message || 'Something went wrong. Please try again.')
      } finally {
        setIsLoading(false)
      }
    }
    
    // Handle button click for basic variant
    const handleBasicButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      // Skip email signup logic for book demo button
      if (isBookDemoButton) {
        if (onClick) {
          onClick(e)
        }
        return
      }
      if (emailSignupVariant === 'lean' && !usedInHeader) {
        // e.stopPropagation()
        return document.getElementById('hero-email-form')?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
      if (onClick) {
        onClick(e)
      }
      setShowEmailInput(true)
    }
    
    // Size styles
    const sizeStyles = {
      header: "px-4 py-2 text-sm font-semibold",
      hero: "px-8 py-3 lg:text-base text-sm font-semibold",
      section: "px-6 py-2.5 lg:text-lg text-base font-medium",
      sm: "px-3 py-1.5 text-sm font-medium",
      lg: "px-8 py-3 text-base font-semibold lg:px-10 lg:py-4 lg:text-lg"
    }

    // Variant styles
    const variantStyles = {
      primary: "gradient-button_VWO bg-white text-black hover:bg-gray-50 border-white",
      secondary: "bg-black text-white border-brand-border border-1",
      outline: "border border-white text-white bg-transparent ",
      ghost: "text-white hover:bg-white/10"
    }

    const buttonClasses = cn(

      " inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md transition-all duration-200",
      "hover:scale-105 hover:-translate-y-0.5 shadow-lg hover:shadow-xl",
      "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
      "disabled:pointer-events-none disabled:opacity-50",
      sizeStyles[size],
      variantStyles[variant],
      className,
      ""
    )

    // Determine the id to use
    const computedId = isLoginMetric(href) ? BUTTON_IDS.GET_STARTED : id

    // Check if this is the book demo button (should work as originally, bypass email signup)
    const isBookDemoButton = computedId === 'book-demo-button' || id === 'book-demo-button'

    // Render email signup UI if enabled (but not for book demo button)
    if (enableEmailSignup && !isBookDemoButton) {
      // Basic variant: Show button that reveals email input
        if (!showEmailInput ) {
        return (
          <button
            type="button"
            className={buttonClasses}
            ref={ref as React.Ref<HTMLButtonElement>}
            id={computedId}
            onClick={handleBasicButtonClick}
            {...props}
          >
            {children}
          </button>
        )
      }
      
      // Lean/Instant variants or Basic after button click: Show email input form
      return (
        <div className="w-full sm:w-auto" id={emailSignupVariant === 'lean' ? 'hero-email-input-container' : undefined}>
          {success ? (
            // Success state - replaces the form to prevent layout shift
            <EmailSignupSuccess
              onRetry={() => {
                setSuccess(false)
                setEmail('')
                setError(null)
                // Focus input after reset
                setTimeout(() => {
                  emailInputRef.current?.focus()
                }, 100)
              }}
            />
          ) : (
            <form onSubmit={handleEmailSubmit} className="w-full sm:w-auto">
              <div className="flex flex-col gap-1.5 w-full sm:w-auto">
                <div className="relative flex items-center w-full sm:w-auto">
                  {/* Back button for basic variant */}
                  {/* {emailSignupVariant === 'lean' && ( */}
                    <button
                      type="button"
                      onClick={() => {
                        setShowEmailInput(false)
                        setEmail('')
                        setError(null)
                        setSuccess(false)
                      }}
                      className="mr-2 p-1.5 rounded-md hover:bg-white/10 text-white/70 hover:text-white transition-colors"
                      aria-label="Go back"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                  {/* )} */}
                  <input
                    ref={emailInputRef}
                    id={emailSignupVariant === 'lean' ? 'hero-email-input' : undefined}
                    type="email"
                    autoFocus={true}
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value)
                      setError(null)
                    }}
                    placeholder="Enter your email"
                    disabled={isLoading}
                    className={cn(
                      "px-4 py-2.5 text-sm rounded-l-md rounded-r-none border border-white/20 bg-black/50 text-white placeholder:text-white/60",
                      "outline-none h-[48px]",
                      "disabled:opacity-50 disabled:cursor-not-allowed",
                      "w-full sm:w-auto sm:min-w-[240px]",
                      emailSignupVariant === 'basic' && "sm:min-w-[200px]"
                    )}
                    required
                  />
                  <button
                    type="submit"
                    disabled={isLoading || !email.trim()}
                    className={cn(
                      "px-3 py-2.5 text-sm font-semibold rounded-r-md rounded-l-none h-[48px]",
                      "gradient-button_VWO bg-white text-black hover:bg-gray-50 border-white border-l-0",
                      "focus:outline-none focus:ring-2 focus:ring-white/50",
                      "disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200",
                      "whitespace-nowrap flex items-center justify-center",
                      (isLoading || !email.trim()) && "opacity-50 cursor-not-allowed"
                    )}
                  >
                    {isLoading ? (
                      <span className="flex items-center gap-2">
                        <span className="animate-spin">⏳</span>
                        <span>Sending...</span>
                      </span>
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                  </button>
                </div>
                {error && (
                  <p className="text-red-400 text-xs px-1">{error}</p>
                )}
              </div>
            </form>
          )}
        </div>
      )
    }

    // Handle Google login click
    const handleGoogleLoginClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
      console.log('🔵 CTAButton: handleGoogleLoginClick called', { enableGoogleLogin, hasOnClick: !!onClick })
      e.preventDefault()
      e.stopPropagation()
      
      if (onClick) {
        console.log('🔵 CTAButton: Calling onClick prop')
        onClick(e)
      }
      
      if (!enableGoogleLogin) {
        console.warn('🔵 CTAButton: enableGoogleLogin is false, skipping Google login')
        return
      }
      
      console.log('🔵 CTAButton: Starting Google login flow...')
      
      try {
        console.log('🔵 CTAButton: Attempting dynamic import...')
        // Dynamic import to avoid loading this on pages that don't need it
        const googleSigninModule = await import('@/app/(rodrigo)/w45-deepwork-google/GoogleSignin')
        console.log('🔵 CTAButton: Dynamic import successful', { hasInitiateGooglePopupSignIn: !!googleSigninModule.initiateGooglePopupSignIn })
        
        if (!googleSigninModule.initiateGooglePopupSignIn) {
          throw new Error('initiateGooglePopupSignIn function not found in module')
        }
        
        console.log('🔵 CTAButton: Calling initiateGooglePopupSignIn...')
        await googleSigninModule.initiateGooglePopupSignIn()
        console.log('🔵 CTAButton: initiateGooglePopupSignIn completed successfully')
      } catch (error: any) {
        console.error('🔴 CTAButton: Google login failed', error)
        // Show user-friendly error message
        const errorMessage = error?.message || 'Failed to sign in with Google. Please try again.'
        alert(errorMessage) // TODO: Replace with a proper toast/notification component
        
        // Log additional context for debugging
        console.error('🔴 CTAButton: Google login error details:', {
          message: error?.message,
          code: error?.code,
          stack: error?.stack,
          userAgent: typeof window !== 'undefined' ? window.navigator.userAgent : 'unknown',
          errorType: error?.constructor?.name,
          errorString: String(error),
        })
        
        // Re-throw to ensure error is visible
        throw error
      }
    }

    // If Google login is enabled, always render as button (ignore href)
    if (enableGoogleLogin) {
      // Log on render to verify prop is passed
      if (typeof window !== 'undefined') {
        console.log('🟡 CTAButton: Rendering with enableGoogleLogin=true', { 
          id: computedId, 
          showGoogleLogo,
          googleLogoColor: logoColor,
          hasHref: !!href,
          href: href
        })
      }
      
      // Remove href and type from props since we're rendering a button
      const { href: _, type, ...buttonProps } = props as any
      
      return (
        <button
          type="button"
          className={buttonClasses}
          ref={ref as React.Ref<HTMLButtonElement>}
          id={computedId}
          onClick={handleGoogleLoginClick}
          {...buttonProps}
        >
          {showGoogleLogo && <GoogleLogo color={logoColor} />}
          {children}
        </button>
      )
    }

    // Handle scroll to input for lean variant when clicking other CTAs
    const handleLeanVariantScroll = (e: React.MouseEvent) => {
      // Skip scroll handling for book demo button
      if (isBookDemoButton) {
        return
      }
      if (emailSignupVariant === 'lean' && !enableEmailSignup) {
        e.preventDefault()
        const heroInput = document.getElementById('hero-email-input')
        if (heroInput) {
          heroInput.scrollIntoView({ behavior: 'smooth', block: 'center' })
          setTimeout(() => {
            heroInput.focus()
          }, 500)
        } else {
          window.scrollTo({ top: 0, behavior: 'smooth' })
        }
      }
    }

    if (href) {
      // Use InternalLink for ALL app.hypertask.ai URLs to enable PostHog session tracking
      if (isAppHypertaskUrl(href)) {
        return (
          <InternalLink
            href={href}
            external={external}
            className={buttonClasses}
            ref={ref as React.Ref<HTMLAnchorElement>}
            id={computedId}
            onClick={handleLeanVariantScroll}
            {...(props as React.AnchorHTMLAttributes<HTMLAnchorElement>)}
          >
            {children}
          </InternalLink>
        )
      }
      
      if (href.startsWith('http') || external) {
        // Only pass anchor-allowed props to <a>
        const { type, ...anchorProps } = props as React.AnchorHTMLAttributes<HTMLAnchorElement>
        return (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className={buttonClasses}
            ref={ref as React.Ref<HTMLAnchorElement>}
            id={computedId}
            onClick={handleLeanVariantScroll}
            {...anchorProps}
          >
            {children}
          </a>
        )
      }
      
      return (
        <Link href={href} onClick={handleLeanVariantScroll}>
          <span
            className={buttonClasses}
            ref={ref as React.Ref<HTMLSpanElement>}
            id={computedId}
            {...props}
          >
            {children}
          </span>
        </Link>
      )
    }

    return (
      <button
        className={buttonClasses}
        ref={ref as React.Ref<HTMLButtonElement>}
        id={computedId}
        {...props}
      >
        {children}
      </button>
    )
  }
)
CTAButton.displayName = "CTAButton"

// CTA Group component
interface CTAGroupProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode
  orientation?: "horizontal" | "vertical"
  className?: string
}

export const CTAGroup = React.forwardRef<HTMLDivElement, CTAGroupProps>(
  ({ children, className, orientation = "horizontal", ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          "flex items-center gap-4",
          orientation === "horizontal" ? "flex-col sm:flex-row" : "flex-col",
          className
        )}
        {...props}
      >
        {children}
      </div>
    )
  }
)
CTAGroup.displayName = "CTAGroup"
